import { useState } from 'react'
import {Send, ShieldCheck, Award, FileCheck2, ArrowRight} from 'lucide-react'

function XLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}

const C = {
  bgDeep:     '#000000',
  surface:    '#05080a',
  turq:       '#1ad6c4',
  turqBright: '#3ce8d6',
  turqGlow:   'rgba(26,214,196,0.35)',
  turqDim:    'rgba(26,214,196,0.12)',
  borderTurq: 'rgba(26,214,196,0.30)',
  border:     'rgba(255,255,255,0.07)',
  borderMed:  'rgba(255,255,255,0.12)',
  textPrimary:'#ffffff',
  textSecond: '#b4c0cc',
  textMuted:  '#6a7682',
}

const LOGO    = 'https://static.lumi.new/9c/9c1ccefd81b9dbb855ed621cfae893f3.webp'
const HERO_BG = 'https://static.lumi.new/69/699e93a880cabd9b4a968378d1b88c64.png'

const FEATURES = [
  { icon: ShieldCheck, title: 'In-Depth Security Audits', desc: 'Comprehensive coverage across all major EVM attack surfaces and vulnerability classes.' },
  { icon: FileCheck2,  title: 'Full Vulnerability Report', desc: 'Detailed findings, severity levels, and a transparent 1–100 score.' },
  { icon: Award,       title: 'Verified Badge & Registry', desc: 'A public badge and permanent listing on our audited projects registry.' },
]

export default function ComingSoon() {
  const [email, setEmail]     = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [focused, setFocused] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (email.trim()) setSubmitted(true)
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{ background: C.bgDeep, color: C.textPrimary }}
    >
      {/* Background */}
      <img
        src={HERO_BG}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
        style={{ opacity: 0.45 }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(180deg, ${C.bgDeep} 0%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.6) 70%, ${C.bgDeep} 100%)` }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(90deg, ${C.bgDeep} 0%, transparent 18%, transparent 82%, ${C.bgDeep} 100%)` }}
      />
      {/* Turquoise glow at bottom */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{ width: '60%', height: '40%', background: `radial-gradient(ellipse at 50% 100%, rgba(26,214,196,0.22) 0%, transparent 70%)`, filter: 'blur(40px)' }}
      />

      {/* Navbar */}
      <header className="relative z-10 flex items-center justify-center px-6 py-6">
        <img
          src={LOGO}
          alt="Mandox Audits"
          className="h-16 sm:h-20 md:h-24 w-auto object-contain select-none drop-shadow-lg"
          draggable={false}
          style={{ filter: `drop-shadow(0 0 18px rgba(26,214,196,0.45))` }}
        />
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 py-16 text-center">

        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-8"
          style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}`, color: C.turqBright, letterSpacing: '0.1em' }}
        >
          <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
          EVM Smart Contract Security
        </div>

        {/* Heading */}
        <h1
          className="text-white mx-auto max-w-[720px] leading-[1.04] tracking-tight mb-4"
          style={{ fontSize: 'clamp(38px, 6.5vw, 72px)', fontWeight: 300, letterSpacing: '-1.5px' }}
        >
          Something big is{' '}
          <span style={{ color: C.turq }}>coming soon.</span>
        </h1>

        <p
          className="mt-4 leading-relaxed mx-auto max-w-[500px] mb-10"
          style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: C.textSecond, fontWeight: 300 }}
        >
          Mandox Audits is launching a professional EVM smart contract auditing service — verified badges, full reports, and a public registry your investors can trust.
        </p>

        {/* Email capture */}
        <div className="w-full max-w-[420px] mx-auto mb-14">
          {submitted ? (
            <div
              className="flex items-center justify-center gap-2.5 px-5 py-4 rounded-xl text-sm font-medium"
              style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}`, color: C.turqBright }}
            >
              <ShieldCheck className="w-4 h-4 flex-shrink-0" />
              You're on the list — we'll notify you at launch!
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex gap-2">
              <div
                className="flex-1 flex items-center gap-2 px-4 rounded-lg transition-all"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${focused ? C.borderTurq : C.borderMed}`,
                  boxShadow: focused ? `0 0 0 3px ${C.turqDim}` : 'none',
                }}
              >
                <input
                  type="email"
                  required
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  className="flex-1 bg-transparent outline-none text-sm py-3"
                  style={{ color: C.textPrimary, fontSize: '16px' }}
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-95 flex-shrink-0"
                style={{ background: C.turq, color: '#000000', boxShadow: `0 0 24px rgba(26,214,196,0.35)` }}
              >
                Notify Me <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
          <p className="text-xs mt-2.5" style={{ color: C.textMuted }}>
            No spam. We'll only reach out when we launch.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid sm:grid-cols-3 gap-4 w-full max-w-[860px] mx-auto">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-xl p-5 text-left flex flex-col gap-3"
              style={{ background: 'rgba(5,8,10,0.85)', border: `1px solid ${C.border}`, backdropFilter: 'blur(12px)' }}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
              >
                <f.icon className="w-4 h-4" style={{ color: C.turq }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white mb-1">{f.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: C.textMuted }}>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Preview section */}
        <div className="w-full max-w-[860px] mx-auto mt-16 px-2">
          <p className="text-xs font-semibold tracking-widest uppercase mb-4 text-center" style={{ color: C.turq, letterSpacing: '0.12em' }}>
            Preview
          </p>
          <h2 className="text-lg font-semibold text-white text-center mb-6" style={{ letterSpacing: '-0.3px' }}>
            What your audit profile looks like
          </h2>
          <div
            className="relative rounded-2xl overflow-hidden mx-auto"
            style={{
              border: `1px solid ${C.borderTurq}`,
              boxShadow: `0 0 60px rgba(26,214,196,0.12), 0 0 0 1px ${C.borderTurq}`,
              maxWidth: 480,
            }}
          >
            {/* Top glow bar */}
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${C.turq}, transparent)` }} />
            <img
              src="https://static.lumi.new/eb/eb505b445cb78d12db5e91f52d0a7eee.webp"
              alt="Example audit profile showing Mandox with a 90/100 security score, Audit Passed verdict, and security checklist"
              className="w-full block"
              style={{ display: 'block' }}
            />
          </div>
          <p className="text-xs text-center mt-4" style={{ color: C.textMuted }}>
            Every audited project gets a verified public profile with score, verdict, checklist &amp; PDF report.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-[1060px] mx-auto w-full">
        <p className="text-xs" style={{ color: C.textMuted }}>
          © {new Date().getFullYear()} Mandox Audits. All rights reserved.
        </p>
        <div className="flex items-center gap-5">
          <a
            href="https://x.com/Mandox_Audits"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-white"
            style={{ color: C.textMuted }}
            aria-label="Follow Mandox Audits on X"
          >
            <XLogo className="w-4 h-4" />
          </a>
          <a
            href="https://t.me/officialmandox"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-white"
            style={{ color: C.textMuted }}
            aria-label="Join Mandox Telegram"
          >
            <Send className="w-4 h-4" />
          </a>
          <a
            href="mailto:MandoxAudits@gmail.com"
            className="text-xs transition-colors hover:text-white"
            style={{ color: C.textMuted }}
          >
            MandoxAudits@gmail.com
          </a>
        </div>
      </footer>
    </div>
  )
}
